import { useState } from "react";
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Alert,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Iconify from "../../../components/iconify";
import { post } from "src/utils/networkUtils";

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // MFA step
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  const handleLogin = () => {
    setError(null);
    setLoading(true);
    post("/admin-auth/login", { email: email.trim(), password })
      .then((response) => {
        const data = response?.data ?? response;
        if (data.requiresMfa) {
          setMfaToken(data.mfaToken);
        } else {
          localStorage.setItem("adminToken", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          const redirectUrl =
            localStorage.getItem("redirectAfterLogin") || "/dashboard/home";
          localStorage.removeItem("redirectAfterLogin");
          window.location.href = redirectUrl;
        }
      })
      .catch((err) => {
        setPassword("");
        const msg =
          err?.response?.data?.message ?? err?.message ?? "Login failed";
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  const handleMfaVerify = () => {
    setError(null);
    setLoading(true);
    post("/admin-auth/mfa/verify", { mfaToken, code: mfaCode.trim() })
      .then((response) => {
        const data = response?.data ?? response;
        storeSession(data.token, data.session);
      })
      .catch((err) => {
        setMfaCode("");
        const msg =
          err?.response?.data?.message ?? err?.message ?? "Invalid MFA code";
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  const storeSession = (token, user) => {
    localStorage.setItem("adminToken", token);
    localStorage.setItem("user", JSON.stringify(user));
    window.location.href = "/dashboard/home";
  };

  if (mfaToken) {
    return (
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          Enter the 6-digit code from your authenticator app.
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          autoFocus
          name="mfa_code"
          label="Authenticator code"
          value={mfaCode}
          inputProps={{ maxLength: 8 }}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) =>
            e.key === "Enter" && mfaCode.length >= 6 && handleMfaVerify()
          }
        />

        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={loading}
          disabled={mfaCode.length < 6}
          onClick={handleMfaVerify}
        >
          Verify
        </LoadingButton>

        <LoadingButton
          size="small"
          variant="text"
          onClick={() => {
            setMfaToken(null);
            setError(null);
            setMfaCode("");
          }}
        >
          Back to login
        </LoadingButton>
      </Stack>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          name="email"
          label="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && password.length > 0 && handleLogin()
          }
        />

        <TextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && email.length > 0 && handleLogin()
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ my: 2 }}
      />

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={loading}
        disabled={email.length === 0 || password.length === 0}
        onClick={handleLogin}
      >
        Login
      </LoadingButton>
    </>
  );
}
